import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { CardAttachment, CardGoogleClassroom } from "@shared/models/card-stack"
import type { GoogleClass, GoogleClassTopic } from "@shared/models/google-classroom"
import type { Planbook } from "@shared/models/planbook"
import { handleError } from "cc-frontend/error-handling"
import BelongTo from "cc-frontend/helpers/belong-to"
import FindDocument from "cc-frontend/helpers/find-doc"
import type { GoogleClassroomCardEditor } from "cc-frontend/lib/google-classroom/card-editor"
import type NotifierService from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import { use } from "ember-could-get-used-to-this"
import { includes, isNil, map } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import { tracked, TrackedArray } from "tracked-built-ins"
import styles from "./main.module.css"

interface Signature {
  Args: {
    dateString: string
    cardEditor: GoogleClassroomCardEditor
    cardStackId: string
    planbook: Planbook
    card: CardGoogleClassroom
    closeModal: Function
    timeZoneToPostIn: string
  }
}

export default class GoogleClassroomBodyComponent extends Component<Signature> {
  readonly styles = styles
  /* Services */
  @service declare rpc: Rpc
  @service declare notifier: NotifierService
  @service declare dialog

  /* Tracked */
  @tracked canShowAddAttachmentMenu: boolean = false
  @tracked cachedClasses = new TrackedArray([]) as GoogleClass[]
  @tracked cachedTopics = new TrackedArray([]) as string[]
  @tracked googleClassroomClassesIsFetched = false
  @tracked googleClassroomClassTopicsIsFetched = false
  @tracked isUpdatingSchedule = false

  @use mediator = new FindDocument(() => ["google-classroom-card-mediator", this.args.card.id])
  @use rotationCalendar = new BelongTo(() => [this.args.planbook, "rotationCalendar"])

  get googleClassroomClasses(): Array<GoogleClass> | null {
    if (this.googleClassroomClassesIsFetched === false) {
      this.rpc
        .google_classroom_fetch_class_list({ planbookId: this.args.planbook.id })
        .then((response) => {
          if (response.responseOk) {
            this.cachedClasses = response.result.classes
            this.googleClassroomClassesIsFetched = true
          } else {
            console.log("Error", response)
          }
        })
        .catch((error) => {
          console.error("You don't have any GC classes/we couldn't find them", error)
        })
    }
    // @ts-ignore setting this to the typed tracked array is tricky
    return this.cachedClasses
  }

  get googleClassroomClassTopics(): Array<GoogleClassTopic> | null {
    if (this.googleClassroomClassTopicsIsFetched === false) {
      this.rpc
        .google_classroom_fetch_class_topics_list({ planbookId: this.args.planbook.id })
        .then((response) => {
          if (response.responseOk) {
            this.cachedTopics = R.pipe(
              response.result.topics,
              R.map((topic) => topic.name),
              R.uniq(),
              R.sortBy((t) => t)
            )
            this.googleClassroomClassTopicsIsFetched = true
          } else {
            console.log("Error", response)
          }
        })
        .catch((error) => {
          console.error("You don't have any GC class topics and/or we couldn't find them", error)
        })
    }
    // @ts-ignore setting this to the typed tracked array is tricky
    return this.cachedTopics
  }

  get sortedRotationCalendarDates() {
    if (isNil(this.rotationCalendar) || isNil(this.rotationCalendar.content)) return

    return R.pipe(
      this.rotationCalendar.content?.attributes.dates,
      R.sortBy((date) => date.id),
      R.filter((date) => date.attributes.dateString >= this.args.dateString)
    )
  }

  get attachments() {
    return this.args.card.attributes.attachments
  }

  get cannotEditAttachmentsOrMultipleChoiceItems(): boolean {
    if (isNil(this.mediator) || isNil(this.mediator.content)) return false
    let currentPostStatuses = map(
      this.mediator.content.attributes.googleClassStatuses,
      (classStatus) => {
        return classStatus.status
      }
    )

    return includes(currentPostStatuses, "PUBLISHED")
  }

  @action
  updateScheduled() {
    this.isUpdatingSchedule = true
    this.rpc
      .google_classroom_update_card({
        cardId: this.args.card.id,
        cardStackId: this.args.cardStackId,
      })
      .then((response) => {
        if (response.responseOk) {
          console.log("Success!!", response)
          this.args.closeModal()
        } else {
          this.notifier.success("", {
            title: `Oh no! Something went wrong during the update!`,
            message: "Please try again in a moment.",
            duration: 5000,
            type: "is-error",
          })
        }
      })
      .finally(() => {
        console.log("finally")
        this.isUpdatingSchedule = false
      })
      .catch((error) => {
        console.error("Error", error.message)
        this.notifier.success("", {
          title: `Oh no! This card was unable to update!`,
          message: "Please try again later.",
          duration: 5000,
          type: "is-error",
        })
      })
  }

  @action
  toggleAddAttachmentMenu() {
    if (this.isDestroyed || this.isDestroying) return
    this.canShowAddAttachmentMenu = !this.canShowAddAttachmentMenu
  }

  @action
  openedAttachDialog() {
    if (this.isDestroyed || this.isDestroying) return
    this.canShowAddAttachmentMenu = false
  }

  @action
  addAttachment(source: string, files: []) {
    this.canShowAddAttachmentMenu = false
    this.addAttachmentInternal(source, files)
  }

  @action
  addAttachmentAsLink() {
    this.canShowAddAttachmentMenu = false
    this.dialog
      .blank("dialogs/link-as-attachment", {
        className: "dialog--create-annotation",
        title: null,
        url: null,
      })
      .then((response) => {
        let files = [{ url: response.url, title: response.title, type: "link" }]
        this.addAttachmentInternal("link", files as Array<Omit<CardAttachment, "id">>)
      })
  }

  private addAttachmentInternal(source: string, files: Array<Omit<CardAttachment, "id">>): void {
    // The Google Classroom API will fail with "Duplicate materials are not allowed"
    // if the user attaches the same Google Drive/Docs file twice. Check for this.

    const driveIdFromUrl = (url: string): string | null => {
      if (!/(drive|docs)\.google\.com/.test(url)) return null
      // The ID usually comes after /d/, but fail open if it doesn't look right.
      const match = url.match(/\/d\/([^/?#]+)/)
      if (!match || match[1].length < 20) return null
      return match[1]
    }

    const existingDriveIds = new Set(
      this.args.card.attributes.attachments
        .map((a) => driveIdFromUrl(a.url))
        .filter((id): id is string => id !== null)
    )
    const uniqueFiles = files.filter((f) => {
      const id = driveIdFromUrl(f.url)
      return id === null || !existingDriveIds.has(id)
    })

    const duplicateCount = files.length - uniqueFiles.length
    if (duplicateCount > 0) {
      this.notifier.success("", {
        title: "Duplicate attachments skipped",
        message:
          duplicateCount > 1
            ? `We skipped adding ${duplicateCount} Google Drive files that are already attached to this card.`
            : "We skipped adding a Google Drive file that is already attached to this card.",
        duration: 5000,
        type: "is-warning",
      })
    }

    if (uniqueFiles.length === 0) return

    this.args.cardEditor.addAttachments(uniqueFiles, source)
  }

  @action
  changeShareMode(attachment, shareMode) {
    this.args.cardEditor.upsertShareMode(attachment.id, shareMode)
  }

  @action
  removeAttachment(_cardId, attachment) {
    window
      .swal({
        title: "Delete File?",
        text: "Are you sure you want to delete this file?",
        type: "warning",
        showCancelButton: true,
      })
      .then(() => {
        this.args.cardEditor.removeAttachment(attachment)
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::GoogleClassroom::Main": typeof GoogleClassroomBodyComponent
  }
}
